import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getError } from '../../../utils/FormError';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';

import './Select.scss';

export default function Select({
  label,
  name,
  options = [],
  className = '',
  required = false,
  disabled = false,
  validationRules = {},
  placeholder = 'Choose an option...',
  onChange,
  // Static option lists are short, so the field is a plain click-to-open picker by default;
  // pass `searchable` to let the user type to filter a longer list instead.
  searchable = false,
}) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const instanceId = useRef(Math.random().toString(36));
  const isInitialized = useRef(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const { register, formState: { errors }, setValue, getValues, watch } = useFormContext();
  const error = name ? getError(errors, name) : null;

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [selected, setSelected] = useState(null);

  const watchedValue = watch(name);

  useEffect(() => {
    if (!options || options.length === 0) return;

    const currentValue = watchedValue ?? getValues(name);
    const found = options.find(opt => opt.value === currentValue);

    if (found) {
      setSelected(found);
      setFilter(found.label);
    } else if (options.length > 0) {
      const first = options[0];
      setSelected(first);
      setFilter(first.label);
      setValue(name, first.value, { shouldValidate: true, shouldDirty: true });
      if (onChange && isInitialized.current) onChange(first.value);
    } else {
      setSelected(null);
      setFilter('');
      if (onChange && isInitialized.current) onChange(null);
    }
    isInitialized.current = true;
  }, [watchedValue, options]);

  // Close when another dropdown opens
  useEffect(() => {
    if (isOpen) {
      document.dispatchEvent(new CustomEvent('dropdownOpen', { detail: { id: instanceId.current } }));
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail.id !== instanceId.current) setIsOpen(false);
    };
    document.addEventListener('dropdownOpen', handler);
    return () => document.removeEventListener('dropdownOpen', handler);
  }, []);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const isRtl = document.documentElement.dir === 'rtl';
    // In RTL documents the scrollbar can render on the left, so window.innerWidth
    // (which always includes the scrollbar's width) throws off a right-edge
    // calculation — document.documentElement.clientWidth excludes it and matches
    // the coordinate space getBoundingClientRect() already uses.
    const left = isRtl ? document.documentElement.clientWidth - rect.right : rect.left;
    setDropdownPos({ top: rect.bottom + 2, left, width: rect.width, isRtl });
  }, []);

  useEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const filteredOptions = options.filter(opt => {
    if (!isOpen) return false;
    if (filter === '' || filter === selected?.label) return true;
    return opt.label.toLowerCase().includes(filter.toLowerCase());
  });

  const handleSelect = (opt, index) => {
    setHighlightIndex(index);
    setSelected(opt);
    setFilter(opt.label);
    setValue(name, opt.value, { shouldValidate: true, shouldDirty: true });
    if (onChange) onChange(opt.value);
    setIsOpen(false);
  };

  const registration = register(name, validationRules);

  return (
    <div className={`position-relative w-100 ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={name} className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <div className={`input-group custom_select ${disabled ? 'disabled' : ''}`}>
        <div style={{ position: 'relative', flex: '1 1 auto', width: '1%', minWidth: 0 }}>
          <input
            type="text"
            id={name}
            autoComplete="off"
            className={`form-control ${error ? 'is-invalid' : ''}`}
            placeholder={selected ? '' : placeholder}
            value={filter}
            readOnly={!searchable}
            onChange={(e) => {
              if (!searchable) return;
              setFilter(e.target.value);
              setIsOpen(true);
              setHighlightIndex(0);
              if(onChange) onChange(null); // reset car c'est un filtre libre, pas une valeur sélectionnée
            }}
            onClick={() => {
              if (disabled) return;
              // not searchable: the field acts as a button, so a second click closes the list
              setIsOpen(prev => (searchable ? true : !prev));
              setHighlightIndex(0);
            }}
            disabled={disabled}
            ref={(el) => {
              inputRef.current = el;
              if (typeof registration.ref === 'function') registration.ref(el);
            }}
            onBlur={registration.onBlur}
            style={{
              width: '100%',
              ...(!searchable && !disabled && { cursor: 'pointer', caretColor: 'transparent', backgroundColor: '#fff' }),
            }}
          />
          {selected?.icon && !isOpen && (
            <div
              style={{
                position: 'absolute', inset: '1px', display: 'flex', alignItems: 'center', gap: 12,
                padding: '0 0.75rem', background: disabled ? '#e9ecef' : '#fff', pointerEvents: 'none',
                overflow: 'hidden', whiteSpace: 'nowrap',
                borderTopLeftRadius: 'calc(0.375rem - 1px)', borderBottomLeftRadius: 'calc(0.375rem - 1px)',
              }}
            >
              {selected.icon}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.9em' }}>{selected.label}</span>
            </div>
          )}
        </div>
        <div
          className={`input-group-text ${isOpen ? 'open' : ''}`}
          onClick={() => {
            if (disabled) return;
            setIsOpen((prev) => !prev);
            setHighlightIndex(0);
          }}
        >
          <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
        </div>
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="dropdown-menu show p-0"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            ...(dropdownPos.isRtl
              ? { right: dropdownPos.left }
              : { left: dropdownPos.left }),
            width: dropdownPos.width,
            maxHeight: '320px',
            overflowY: 'auto',
            zIndex: 9999,
          }}
        >
          <ul className="list-unstyled mb-0">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`dropdown-item d-flex justify-content-between px-3 py-2 ${
                      opt.value == selected?.value ? 'bg-primary text-white' : ''
                    }`}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(opt, i); }}
                  >
                    <span className="d-flex align-items-center gap-2">
                      {opt.icon}
                      <span className="d-flex flex-column">
                        <span>{opt.label}</span>
                        {opt.description && (
                          <span
                            style={{
                              fontSize: '0.78em',
                              fontWeight: 400,
                              color: opt.value == selected?.value ? 'rgba(255,255,255,0.85)' : '#6b7280',
                            }}
                          >
                            {opt.description}
                          </span>
                        )}
                      </span>
                    </span>
                    <span style={{ width: '1em', flexShrink: 0, textAlign: 'center' }}>
                      {selected?.value === opt.value && <FontAwesomeIcon icon={faCheck} />}
                    </span>
                  </button>
                </li>
              ))
            ) : (
              <li className="dropdown-item text-muted">No option</li>
            )}
          </ul>
        </div>,
        document.body
      )}

      {error && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  );
}
